/**
 * Single source of truth for feature-flag keys + their fail-closed defaults.
 * Flag `key` must match the flag name authored in Flagsmith exactly.
 * Defaults are returned whenever Flagsmith is unreachable or the flag is absent.
 */
export interface FeatureFlagDefinition {
  /** Flagsmith flag name. */
  key: string;
  /** Value returned when the provider is unavailable or the flag is missing. */
  defaultValue: boolean;
  /** Human description — what this flag gates. */
  description: string;
}

export const FLAGS = {
  designSystemPage: {
    key: 'design-system-page',
    defaultValue: true,
    description: 'Exposes the /design-system showcase route.',
  },
  onboardingFlow: {
    key: 'onboarding-flow',
    defaultValue: false,
    description:
      'Exposes the new 3rd Eye onboarding flow (NPJB-623): the redesigned sign-up at /onboarding/signup with a one-time-code verification, replacing the link. Off means /signup behaves exactly as it does today — the old form, the old endpoint, the emailed link. Fail-closed on purpose: an unreachable Flagsmith must not expose an unfinished flow.',
  },
  intelligenceAudienceSwitcher: {
    key: 'intelligence-audience-switcher',
    defaultValue: true,
    description:
      'Gates the audience-scoped Signal Intelligence surfaces as one feature: the audience page (its picker, saved audiences and the ICP modal — NPJB-586/711/722/723/726) AND the account and contact profile pages, which carry the audience in their path and compute everything against its filters. Also gates the Signal Intelligence tab and the onboarding exits into it. Off hides the tab and sends the audience page and both profile routes home. Fail-closed, so an unreachable Flagsmith hides them. See useIntelligenceAudienceAccess.',
  },
  homePage: {
    key: '3rd-eye-home',
    defaultValue: false,
    description:
      'Exposes the new 3rd Eye Home page at / (NPJB-899): greeting, search and quick actions, plus the Home item in the header. Off sends / to the Campaigns list at /campaigns, keeping the query string. Fail-closed, so an unreachable Flagsmith shows the Campaigns list. See useHomePageAccess.',
  },
  pushToSalesforceAsRfp: {
    // NOTE: "salseforce" is a typo in the flag as authored in Flagsmith. The key must
    // match the dashboard exactly, so do NOT correct the spelling here — rename it in
    // Flagsmith (every environment) first, then update this key in the same commit.
    key: 'push-to-salseforce-as-rfp',
    defaultValue: false,
    description:
      'Exposes the internal "Push to Salesforce as RFP" action (Actions dropdown + audience-card kebab) and its "View RFP in Salesforce" follow-up. See usePushRfpAccess.',
  },
  pushToSalesforceAsOli: {
    key: 'push-to-salesforce-as-oli',
    defaultValue: false,
    description:
      'Exposes the internal "Push to Salesforce as OLI" action (Actions dropdown + audience-card kebab) and its pricing modal. Independent of the RFP flag — an audience can be pushed as an RFP, an OLI, both, or neither. See usePushOliAccess.',
  },
  exploreExport: {
    key: 'explore-export',
    defaultValue: true,
    description:
      'Exposes the Export data button + export modal on Signal Intelligence (NPJB-566). Default on in existing environments — the export feature is already live; this flag lets ops pull it per tenant/segment via a Flagsmith segment on tenantId. See useExportAccess.',
  },
} as const satisfies Record<string, FeatureFlagDefinition>;

export type FeatureFlagName = keyof typeof FLAGS;
